import { Tooltip } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
// import BoundIcon from '@/assets/icons/bound.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import MinusIcon from '@/assets/icons/minus.svg?react'
import ResetIcon from '@/assets/icons/reset.svg?react'
import { Button } from '@/components/base/Button'
import { InlineCopyable } from '@/components/base/InlineCopy'
import { InlineDropDown } from '@/components/base/InlineDropDown'
import { InlineEditable } from '@/components/base/InlineEditable'
import { InlineHoverAction } from '@/components/base/Inlinehover'
import { Modal } from '@/components/base/Modal'
import { message } from '@/components/base/Notification'
import { Trans } from '@/components/Trans'
import { TeamRole } from '@/consts/team'
import { useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { cn } from '@/utils/style'

import {
  assignCredits,
  // bindTeamMember,
  // unbindTeamMember,
  reclaimCredits,
  updateMemberNickname,
  updateMemberNote,
  updateMemberPassword,
  updateMemberRole,
} from '../services/member'
import { MemberStatus } from '../types/types'
import { randomPassword } from '../utils/utils'
import { AssignCreditDialog, type MemberTarget } from './AssignCreditDialog'
import { ReclaimCreditDialog } from './ReclaimCreditDialog'

type Member = {
  id: string
  teamResID: string
  username: string
  role?: TeamRole
  balance?: number
  bindStatus?: boolean
  note?: string
  loginAccount?: string
  loginPassword?: string
  lastLogin?: string
  totalSpending?: number
  status?: MemberStatus
  totalAssignedInstances?: number
  personalInstances?: number

  createdAtSec?: number
  passwordUpdateAtSec?: number
}

function InfoItem({
  label,
  value,
  span = 'md:col-span-3',
}: {
  label: React.ReactNode
  value: React.ReactNode
  span?: string
}) {
  return (
    <div className={`col-span-12 ${span}`}>
      <div className="t-sb text-secondary mb-2">{label}</div>
      <div className=" text-primary t-sm">{value}</div>
    </div>
  )
}

async function resetMemberPassword(loginAccount: string) {
  return {
    account: loginAccount,
    password: randomPassword(),
  }
}
//todo: when api ready
// async function bindMemberAccount(memberId: string) {
//   console.log('bindMemberAccount', memberId)
//   // return await API.bindMemberAccount(memberId)
//   return new Promise<void>((r) => setTimeout(r, 500))
// }
// async function unbindMemberAccount(memberId: string) {
//   console.log('unbindMemberAccount', memberId)
//   // return await API.unbindMemberAccount(memberId)
//   return new Promise<void>((r) => setTimeout(r, 500))
// }
const fmt2 = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const NICKNAME_MAX_LENGTH = 64
const NOTE_MAX_LENGTH = 128
function validateNickname(input: string): string {
  // Input is already trimmed by InlineEditable component

  if (input.length === 0) {
    throw new Error('Name cannot be empty')
  }

  if (input.length > NICKNAME_MAX_LENGTH) {
    throw new Error(`Name cannot exceed ${NICKNAME_MAX_LENGTH} characters`)
  }

  return input
}

function validateNote(input: string): string {
  // Input is already trimmed by InlineEditable component
  // Empty note is allowed

  if (input.length > NOTE_MAX_LENGTH) {
    throw new Error(`Note cannot exceed ${NOTE_MAX_LENGTH} characters`)
  }

  return input
}

export function MemberInfo({
  data,
  onNicknameChange,
  onBalanceChange,
}: {
  data: Member
  onNicknameChange?: (name: string) => void
  onBalanceChange?: (newBalanceCent: number) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState<string>(data?.username ?? '-')
  const [role, setRole] = useState<TeamRole>(data?.role ?? TeamRole.Normal)
  const [note, setNote] = useState<string>(data?.note ? data.note : '-')
  const [bindStatus, _setBindStatus] = useState<boolean>(Boolean(data?.bindStatus ?? false))
  const balanceCent = data?.balance ?? 0
  const [loginPassword, setLoginPassword] = useState<string>('*******')
  const userStore = useUserStore()
  const userRole = useTeamUserRole()
  const [assignOpen, setAssignOpen] = useState(false)
  const [reclaimOpen, setReclaimOpen] = useState(false)
  const [adminBalance, setAdminBalance] = useState<number>(0)

  const [resultOpen, setResultOpen] = useState(false)
  const [newCred, setNewCred] = useState<{ account: string; password: string } | null>(null)

  const balanceCredits = balanceCent / 100
  const creditBalanceText = fmt2(balanceCredits)

  const teamResID = data.teamResID
  const memberID = data.id ?? ''
  const loginAccount = data?.loginAccount ?? '-'

  const totalSpendingText = fmt2(data.totalSpending ?? 0)
  const totalAssignedInstancesText = data?.totalAssignedInstances ?? 0
  const personalInstancesText = data?.personalInstances ?? 0

  const isSelf = loginAccount === userStore.name
  const canAssign = (userRole === TeamRole.Owner || userRole === TeamRole.Admin) && role > userRole
  const canReclaim = canAssign && !isSelf

  const bindClass = cn('t-sb', {
    'text-success': bindStatus,
    'text-warning': !bindStatus,
  })
  const memberCreateTimeAtSec = data.createdAtSec ?? 0
  const memberLastpdatePasswordAtSec = data.passwordUpdateAtSec ?? 0

  const hasUserReset = memberLastpdatePasswordAtSec > memberCreateTimeAtSec

  const getAdminBalance = () => {
    const res = userStore.account.totalBonus.moneyCent ?? 0
    setAdminBalance(res)
  }

  const assignTarget: MemberTarget[] = []
  assignTarget.push({
    id: data.id,
    name: data.username,
    currentCredit: balanceCent,
  })

  useEffect(() => {
    getAdminBalance()
  }, [userStore.account.totalBonus.moneyCent])

  const handleAssignConfirm = async ({
    perMemberAmount,
    memberIds,
    totalCost,
  }: {
    perMemberAmount: number
    memberIds: string[]
    totalCost: number
  }) => {
    try {
      if (!teamResID) throw new Error('No team context')

      await assignCredits(teamResID, memberIds, perMemberAmount)

      const deltaCent = Math.round(perMemberAmount * 100)
      if (memberIds.includes(data.id)) {
        onBalanceChange?.(balanceCent + deltaCent)
      }

      setAdminBalance((prev) => Number((prev - totalCost).toFixed(3)))

      setAssignOpen(false)
      message.success(
        t('team.member.memberInfo.toast.creditsAssigned', 'Assigned credits successfully.'),
      )
    } catch (e) {
      console.error(e)
    }
  }

  const handleReclaimConfirm = async ({
    memberId,
    reclaimAmount,
  }: {
    memberId: string
    reclaimAmount: number
  }) => {
    try {
      if (!teamResID) throw new Error('No team context')

      await reclaimCredits(teamResID, memberId, reclaimAmount)

      const deltaCent = Math.round(reclaimAmount * 100)
      if (memberId === data.id) {
        onBalanceChange?.(balanceCent - deltaCent)
      }

      await userStore.getAccount()

      setReclaimOpen(false)
      message.success(
        t('team.member.memberInfo.toast.creditsReclaimed', 'Credits reclaimed successfully.'),
      )
    } catch (e) {
      console.error(e)
    }
  }

  const updateNickname = async (name: string) => {
    try {
      const validated = validateNickname(name)
      await updateMemberNickname(data.id, validated)
      setName(validated)
      onNicknameChange?.(validated)
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Invalid name')
      throw err
    }
  }

  const updateNote = async (note: string) => {
    try {
      const validated = validateNote(note)
      await updateMemberNote(data.id, validated)
      setNote(validated)
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Invalid note')
      throw err
    }
  }

  const updateRole = async (role: TeamRole) => {
    await updateMemberRole(data.id, role)
    setRole(role)
  }

  const updatePassword = async (password: string) => {
    await updateMemberPassword(data.id, password)
  }

  const handleResetPassword = async () => {
    try {
      await Modal.confirm({
        title: (
          <Trans i18nKey="team.member.memberInfo.modal.resetPasswordTitle">
            Reset Member Password
          </Trans>
        ),
        className: 'w-[520px]',
        content: (
          <div className="space-y-4">
            <p>
              <Trans i18nKey="team.member.memberInfo.modal.resetPasswordContent">
                Are you sure you want to reset password?
              </Trans>
            </p>
            <div className="rounded-lg bg-error/10 p-4 text-secondary">
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <Trans i18nKey="team.member.memberInfo.modal.resetPasswordContent1">
                    Sign the member out of all active sessions
                  </Trans>
                </li>
                <li>
                  <Trans i18nKey="team.member.memberInfo.modal.resetPasswordContent2">
                    Require member to log in again with the new password
                  </Trans>
                </li>
              </ul>
            </div>
          </div>
        ),
        confirmBtnCfg: {
          type: 'danger',
          text: (
            <Trans i18nKey="team.member.memberInfo.modal.resetPasswordConfirm">
              Reset Password
            </Trans>
          ),
        },
      })

      const cred = await resetMemberPassword(data.loginAccount ?? '')
      setNewCred(cred)
      await updatePassword(cred.password)
      setResultOpen(true)
      setLoginPassword(cred.password)
    } catch {
      console.error('Reset password failed')
    }
  }

  const copyAll = async () => {
    if (!newCred) return
    const text = `Login Account: ${newCred.account}\nLogin Password: ${newCred.password}`
    await navigator.clipboard.writeText(text)
  }

  //todo: when api ready
  // const handleBind = async () => {
  //   try {
  //     await Modal.confirm({
  //       title: 'Bind Account',
  //       className: 'w-[520px]',
  //       content: (
  //         <p>
  //           Are you sure you want to bind <span className="text-btn-primary">{data.username}</span>
  //           's account?
  //         </p>
  //       ),
  //       confirmBtnCfg: { type: 'secondary', text: 'Bind' },
  //     })
  //     await bindTeamMember(data.loginAccount ?? '', data.loginPassword ?? '')
  //     setBindStatus(true)
  //     // toast.success('Account bound')
  //   } catch {
  //     /* User clicked Cancel/Close, ignore */
  //   }
  // }

  // const handleUnbind = async () => {
  //   try {
  //     await Modal.confirm({
  //       title: 'Unbind Account',
  //       className: 'w-[520px]',
  //       content: (
  //         <p>
  //           Are you sure you want to unbind{' '}
  //           <span className="text-btn-primary">{data.username}</span>
  //           's account
  //         </p>
  //       ),
  //       confirmBtnCfg: { type: 'danger', text: 'Unbind' },
  //     })
  //     await unbindTeamMember(data.teamResID)
  //     setBindStatus(false)
  //     // Add toast: toast.success('Account unbound')
  //   } catch {
  //     // User clicked Cancel/Close, ignore
  //   }
  // }

  return (
    <div>
      <div className="p-6 border border-neutral-200 rounded-lg shadow-card-100">
        <div className="grid grid-cols-12 gap-x-3 gap-y-3">
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.account">Account</Trans>}
            value={memberID}
            span="col-span-3"
          />
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.name">Name</Trans>}
            value={
              <InlineEditable
                value={name}
                onSave={updateNickname}
                placeholder="Name"
                item="Edit"
                icon={<EditIcon />}
                maxLength={NICKNAME_MAX_LENGTH}
              />
            }
            span="col-span-3"
          />
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.role">Role</Trans>}
            value={<InlineDropDown value={role} onChange={(v) => updateRole(v as TeamRole)} />}
            span="col-span-3"
          />
          <InfoItem
            label={
              <Trans i18nKey="team.member.memberInfo.accountBalance">
                Account Balance (Credit)
              </Trans>
            }
            value={
              <span className="group inline-flex items-center gap-2">
                <span className="t-sm text-primary">
                  {creditBalanceText}{' '}
                  <span className="text-tertiary">
                    <Trans i18nKey="team.member.dialog.credit">Credit</Trans>
                  </span>
                </span>
                {canAssign && (
                  <button
                    type="button"
                    onClick={() => setAssignOpen(true)}
                    className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition t-sm text-highlight inline-flex items-center gap-1 px-1 py-0.5 -m-1 rounded"
                  >
                    <AddIcon />
                    <Trans i18nKey="team.member.memberInfo.assign">Assign</Trans>
                  </button>
                )}
                {canReclaim && (
                  <button
                    type="button"
                    onClick={() => setReclaimOpen(true)}
                    className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition t-sm text-highlight inline-flex items-center gap-1 px-1 py-0.5 -m-1 rounded"
                  >
                    <MinusIcon />
                    <Trans i18nKey="team.member.memberInfo.reclaim">Reclaim</Trans>
                  </button>
                )}
              </span>
            }
            span="col-span-3"
          />

          <InfoItem
            span="col-span-8"
            label={<Trans i18nKey="team.member.memberInfo.note">Note</Trans>}
            value={
              <span className="t-sm text-primary">
                <InlineEditable
                  value={note}
                  onSave={updateNote}
                  placeholder=""
                  item="Edit"
                  icon={<EditIcon />}
                  maxLength={NOTE_MAX_LENGTH}
                />
              </span>
            }
          />

          <div className="col-span-12 flex items-center gap-4 pt-2">
            <div className="t-sb text-tertiary whitespace-nowrap">
              <Trans i18nKey="team.member.memberInfo.loginInformation">Login Information</Trans>
            </div>
            <div className="h-px bg-neutral-200 flex-1" />
          </div>

          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.loginAccount">Login Account</Trans>}
            value={
              <InlineCopyable
                value={loginAccount}
                copyText={loginAccount}
                buttonContent={t('team.member.memberInfo.copy', 'Copy')}
                copiedContent={t('team.member.memberInfo.copied', 'Copied')}
                className="flex w-full max-w-full"
                displayClassName="t-sm text-primary block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                display={
                  <Tooltip
                    title={<div className="t-sm">{loginAccount}</div>}
                    placement="top"
                    mouseEnterDelay={0}
                  >
                    <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                      {loginAccount}
                    </span>
                  </Tooltip>
                }
              />
            }
            span="col-span-3"
          />
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.loginPassword">Login Password</Trans>}
            value={
              hasUserReset ? (
                <div className="inline-flex items-center gap-2">
                  <span>{loginPassword}</span>
                  <span className="t-xs text-secondary">
                    <Trans i18nKey="team.member.memberInfo.resetByUser">Reset by user</Trans>
                  </span>
                </div>
              ) : (
                <InlineHoverAction
                  icon={
                    <span className="w-4 h-4">
                      <ResetIcon />
                    </span>
                  }
                  actionText={<Trans i18nKey="team.member.memberInfo.reset">Reset</Trans>}
                  onAction={handleResetPassword}
                >
                  {loginPassword}
                </InlineHoverAction>
              )
            }
            span="col-span-3"
          />

          {/* Result modal */}
          <Modal open={resultOpen} onClose={() => setResultOpen(false)} className="w-[560px]">
            <Modal.Header>
              <Trans i18nKey="team.member.memberInfo.newLoginDetails">New Login Details</Trans>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-4">
                <Trans i18nKey="team.member.memberInfo.resetPasswordResult">
                  The team member's password has been reset. Please share the new login details with
                  them securely.
                </Trans>
              </p>

              <div className="rounded-lg border border-neutral-200 bg-panel-white">
                <div className="px-4 py-2 t-sb text-secondary border-b border-neutral-200">
                  <Trans i18nKey="team.member.memberInfo.loginInformation">Login Information</Trans>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <div className="t-sb text-secondary mb-2">
                      <Trans i18nKey="team.member.memberInfo.loginAccount">Login Account</Trans>
                    </div>
                    <InlineCopyable
                      value={newCred?.account ?? ''}
                      buttonContent={t('team.member.memberInfo.copy', 'Copy')}
                      copiedContent={t('team.member.memberInfo.copied', 'Copied')}
                    />
                  </div>
                  <div>
                    <div className="t-sb text-secondary mb-2">
                      <Trans i18nKey="team.member.memberInfo.loginPassword">Login Password</Trans>
                    </div>
                    <InlineCopyable
                      value={newCred?.password ?? ''}
                      buttonContent={t('team.member.memberInfo.copy', 'Copy')}
                      copiedContent={t('team.member.memberInfo.copied', 'Copied')}
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button type="outline" onClick={() => setResultOpen(false)} className="min-w-[88px]">
                <Trans i18nKey="team.member.memberInfo.cancel">Cancel</Trans>
              </Button>
              <Button type="secondary" onClick={copyAll} className="min-w-[160px]">
                <Trans i18nKey="team.member.memberInfo.newLoginDetailsCopy">
                  Copy Login Details
                </Trans>
              </Button>
            </Modal.Footer>
          </Modal>
          {/* //TODO: add binding api when backend ready */}
          {/* <InfoItem
            label={<Trans i18nKey="member.bindingStatus">Binding Status</Trans>}
            value={
              bindStatus ? (
                <InlineHoverAction
                  icon={
                    <span className="w-4 h-4">
                      <BoundIcon />
                    </span>
                  }
                  actionText={<Trans i18nKey="member.unbind">Unbind</Trans>}
                  onAction={() => {}}
                >
                  <span className={bindClass}>Bound</span>
                </InlineHoverAction>
              ) : (
                <InlineHoverAction
                  icon={
                    <span className="w-4 h-4">
                      <BoundIcon />
                    </span>
                  }
                  actionText={<Trans i18nKey="member.bind">Bind</Trans>}
                  onAction={()=>{}}
                >
                  <span className={bindClass}>Unbound</span>
                </InlineHoverAction>
              )
            }
            span="col-span-4 md:col-span-3"
          /> */}
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.bindingStatus">Binding Status</Trans>}
            value={
              bindStatus ? (
                <span className={bindClass}>
                  <Trans i18nKey="team.member.memberInfo.bound">Bound</Trans>
                </span>
              ) : (
                <span className={bindClass}>
                  <Trans i18nKey="team.member.memberInfo.unbound">Unbound</Trans>
                </span>
              )
            }
            span="col-span-3 md:col-span-3"
          />

          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.lastLoginTime">Last Login Time</Trans>}
            value={<span className="text-primary break-all">{data.lastLogin}</span>}
            span="col-span-3 md:col-span-3"
          />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-x-3 gap-y-3">
        <div className="col-span-1 border border-neutral-200 rounded-2xl shadow-card-100 p-6">
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.balance">Balance(Credit)</Trans>}
            value={<span className="h5 text-primary break-all">{creditBalanceText}</span>}
            span="col-span-1"
          />
        </div>
        <div className="col-span-1 border border-neutral-200 rounded-lg shadow-card-100 p-6">
          <InfoItem
            label={
              <Trans i18nKey="team.member.memberInfo.totalSpending">Total Spending(Credit)</Trans>
            }
            value={<span className="h5 text-primary break-all">{totalSpendingText}</span>}
            span="col-span-1"
          />
        </div>
        <div className="col-span-1 border border-neutral-200 rounded-lg shadow-card-100 p-6">
          <InfoItem
            label={<Trans i18nKey="team.member.memberInfo.totalInstance">Instances</Trans>}
            value={
              <span className="h5 text-primary break-all">
                {personalInstancesText} /{' '}
                <span className="text-tertiary">{totalAssignedInstancesText}</span>
              </span>
            }
            span="col-span-1"
          />
        </div>
      </div>
      <AssignCreditDialog
        open={assignOpen}
        variant="single"
        adminBalance={adminBalance}
        targets={assignTarget}
        defaultAmount={0}
        onClose={() => setAssignOpen(false)}
        onConfirm={handleAssignConfirm}
      />
      <ReclaimCreditDialog
        open={reclaimOpen}
        target={assignTarget[0]}
        onClose={() => setReclaimOpen(false)}
        onConfirm={handleReclaimConfirm}
      />
    </div>
  )
}
